import { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';

import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import {
	FlyoutMenuItem,
	FlyoutMenuItemContent,
	FlyoutMenuItemTrigger,
} from '@atlaskit/side-nav-items/flyout-menu-item';

import { useFastshiftSelector } from '@/state';

import { useOrganizationsFlyoutAnalytics } from './hooks/use-organizations-flyout-analytics';
import { messages } from './messages';
import { OrganizationsFlyoutContent } from './organizations-flyout-content';

import type { Organization } from './types';

export type OrganizationsFlyoutMenuItemProps = {
	onOrganizationSelect?: (organization: Organization) => void;
	onViewAllOrganizationsSelect?: () => void;
	isSelected: boolean;
};

export const OrganizationsFlyoutMenuItem = ({ isSelected }: OrganizationsFlyoutMenuItemProps) => {
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);

	const { data, error, pending } = useFastshiftSelector((state) => state.organizations);
	const { sendSideNavFlyoutButtonClick, sendOrganizationLinkClick, sendViewAllLinkClick } =
		useOrganizationsFlyoutAnalytics({ isOpen, organizations: data, error, pending });

	const handleOpenChange = useCallback((nextIsOpen: boolean) => {
		setIsOpen(nextIsOpen);
	}, []);

	const handleClose = useCallback(() => {
		setIsOpen(false);
	}, []);

	return (
		<FlyoutMenuItem isOpen={isOpen} onOpenChange={handleOpenChange}>
			<FlyoutMenuItemTrigger
				testId="side-nav-organizations"
				elemBefore={<PeopleGroupIcon label="" />}
				onClick={() => {
					sendSideNavFlyoutButtonClick();
					handleOpenChange(!isOpen);
				}}
				isSelected={isSelected}
			>
				{formatMessage(messages.organizationsNavLabel)}
			</FlyoutMenuItemTrigger>
			<FlyoutMenuItemContent
				containerTestId="organizations-flyout"
				onClose={() => setIsOpen(false)}
			>
				<OrganizationsFlyoutContent
					error={error}
					organizations={data}
					pending={pending}
					onClose={handleClose}
					onOrganizationSelect={sendOrganizationLinkClick}
					onViewAllOrganizationsSelect={sendViewAllLinkClick}
				/>
			</FlyoutMenuItemContent>
		</FlyoutMenuItem>
	);
};
